import { useEffect, useState, useCallback } from "react";
import { deleteUser, getAllAnnotators } from "services/userService";
import { getAnnotatorSampleCount, transferSamples } from "services/sampleService";
import { getUserInfo } from "services/authService";
import { translateApiMessage } from "utils/translateApiMessage";
import { getRoleLabel } from "utils/roleLabels";

export function countActiveAssignedSamples(dataSummary) {
  const summary = dataSummary || [];
  return summary.reduce((count, item) => {
    if (item.status === "Pending" || item.status === "In Progress") {
      return count + item.count;
    }
    return count;
  }, 0);
}

export function getConfirmDeleteMessage(role, t) {
  if (!role) {
    return t(
      "admin.annotators.modals.messages.confirmDeleteUser",
      "Are you sure you want to delete this user?"
    );
  }
  const roleLabel = getRoleLabel(role, t);
  const template = t(
    "admin.annotators.modals.messages.confirmDeleteRole",
    "Are you sure you want to delete this {{role}}?"
  );
  return template.replace("{{role}}", roleLabel);
}

export function getDeleteSuccessMessage(role, t, { superAdminContext = false } = {}) {
  if (superAdminContext && role !== "Annotator") {
    return t("superAdmin.notifications.userDeleted", "User deleted successfully");
  }
  if (role === "Annotator") {
    return t("admin.annotators.modals.success.annotatorDeleted", "Annotator deleted successfully");
  }
  return t("superAdmin.notifications.userDeleted", "User deleted successfully");
}

/**
 * Shared delete-user flow: sample reassignment (when needed) then soft-delete via PATCH.
 */
export function useDeleteUserFlow({
  user,
  open,
  onClose,
  onSuccess,
  t,
  filterAnnotatorsList,
  superAdminContext = false,
}) {
  const loggedUser = getUserInfo();
  const user_id = user ? user.id : null;
  const user_role = user ? user.role : null;

  const [annotatorList, setAnnotatorData] = useState([]);
  const [selectedValue, setSelectedValue] = useState("");
  const [assignedSamples, setAssignedSamples] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, type: "", message: "" });

  const showSnackbar = useCallback((type, message) => {
    setSnackbar({ open: true, type, message });
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false, type: "", message: "" }));
  }, []);

  const translateError = useCallback(
    (message) =>
      translateApiMessage(
        message,
        t,
        t("admin.annotators.modals.errors.deleteUser", "Failed to delete user")
      ),
    [t]
  );

  const ensureSession = useCallback(() => {
    if (!user_id) {
      showSnackbar(
        "warning",
        t("admin.annotators.modals.errors.deleteUserIdRequired", "User id is missing.")
      );
      return false;
    }
    if (!loggedUser?.id) {
      showSnackbar(
        "warning",
        t(
          "admin.annotators.modals.errors.deleteCurrentUserRequired",
          "Your session has expired. Please sign in again."
        )
      );
      return false;
    }
    return true;
  }, [user_id, loggedUser?.id, showSnackbar, t]);

  const runDelete = useCallback(
    async (reassignId = null) => {
      if (!ensureSession()) {
        throw new Error("session");
      }
      const result = await deleteUser(user_id, loggedUser.id, reassignId);
      if (result.status === "success") {
        showSnackbar("success", getDeleteSuccessMessage(user_role, t, { superAdminContext }));
        return result;
      }
      throw new Error(result.message || "Failed to delete user");
    },
    [ensureSession, user_id, loggedUser?.id, user_role, t, superAdminContext, showSnackbar]
  );

  useEffect(() => {
    const fetchDropdownData = async () => {
      if (!user_id) return;
      try {
        const sessionUser = getUserInfo();
        const data = await getAllAnnotators();
        const filtered = filterAnnotatorsList
          ? filterAnnotatorsList(data, user_id, sessionUser)
          : data.filter((a) => a.id !== user_id);
        setAnnotatorData(filtered);

        const response = await getAnnotatorSampleCount(user_id);
        setAssignedSamples(countActiveAssignedSamples(response.data_summary));
      } catch (error) {
        console.error("Failed to fetch annotators dropdown data", error);
      }
    };

    if (open) {
      fetchDropdownData();
    } else {
      setSelectedValue("");
      setLoading(false);
    }
  }, [open, user_id, filterAnnotatorsList]);

  const finishSuccess = useCallback(
    (message) => {
      onClose();
      onSuccess?.(message);
    },
    [onClose, onSuccess]
  );

  const onProceed = async () => {
    if (!selectedValue) {
      showSnackbar(
        "warning",
        t(
          "admin.annotators.modals.messages.selectAnnotatorWarning",
          "Please select annotator from dropdown"
        )
      );
      return;
    }
    if (!ensureSession()) return;

    setLoading(true);
    try {
      const transferResult = await transferSamples(user_id, selectedValue);
      if (transferResult.status !== "success") {
        showSnackbar(
          "error",
          t("admin.annotators.modals.errors.reassignSamples", "Failed to reassign samples")
        );
        return;
      }
    } catch (error) {
      console.error("Failed to re-assign samples", error);
      showSnackbar(
        "error",
        t("admin.annotators.modals.errors.reassignSamples", "Failed to reassign samples")
      );
      return;
    }

    try {
      await runDelete(selectedValue);
      finishSuccess(getDeleteSuccessMessage(user_role, t, { superAdminContext }));
    } catch (error) {
      if (error.message !== "session") {
        console.error("Error while deleting user after reassignment:", error);
        showSnackbar("error", translateError(error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const onConfirm = async () => {
    if (!ensureSession()) return;

    setLoading(true);
    try {
      await runDelete(null);
      finishSuccess(getDeleteSuccessMessage(user_role, t, { superAdminContext }));
    } catch (error) {
      if (error.message !== "session") {
        console.error("Error while deleting user:", error);
        showSnackbar("error", translateError(error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const needsReassign = user_role === "Annotator" && assignedSamples > 0;
  const lblConfirmDel = getConfirmDeleteMessage(user_role, t);

  return {
    user_id,
    user_role,
    annotatorList,
    selectedValue,
    setSelectedValue,
    assignedSamples,
    loading,
    snackbar,
    closeSnackbar,
    needsReassign,
    lblConfirmDel,
    onProceed,
    onConfirm,
    handleCancel: onClose,
  };
}
